<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { stringToColor, textColor } from '@/lib/utils'

import { useNotify } from '@/composables/notify'
import { useStepper } from '@/composables/stepper'
import { useIsUpdated } from '@/composables/object'
import { useNavigator } from '@/composables/navigator'
import { useEscapeClose } from '@/composables/keyboard'
import { useDetectDevice } from '@/composables/device'
import { useArchive } from '@/composables/archive'
import { useClone } from '@/composables/object'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import StepperNavigation from '@/components/shared/StepperNavigation.vue'
import ArchiveWarningDialog from '@/components/shared/ArchiveWarningDialog.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import FileUrlEdit from '@/components/shared/FileUrlEdit.vue'
import CameraGalleryInput from '@/components/shared/CameraGalleryInput.vue'
import ServiceMultiSelect from '@/components/shared/ServiceMultiSelect.vue'
import RateCardInfo from '@/components/shared/RateCardInfo.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import CurrencyInput from '@/components/shared/CurrencyInput.vue'
import FormSectionTitle from '@/components/shared/FormSectionTitle.vue'
import { useChips } from '@/composables/chips'
import { buildTagPayload } from '@/composables/tag'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  changes: {
    type: Object,
    required: false,
    default: () => ({}),
  },
})

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const { step, nextStep, prevStep, markAttempted } = useStepper()
const { notifySuccess, notifyError } = useNotify()
const { getServiceStructureTypeChip } = useChips()

const planningStore = usePlanningStore()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()

const form = ref(null)
const cameraGalleryInput = ref(null)

const editItem = ref(props.modelValue.id ? useClone(props.modelValue) : {
  structure_type: portalStore.serviceStructureTypeEnum.GROUP_PHYSICAL,
  type: portalStore.serviceTypeEnum.EQUIPMENT,
  tags: [],
  components: [],
  currency_ref: webSiteStore.getDefaultCurrency().id,
  availability_type: portalStore.serviceAvailabilityTypeEnum.USUALLY_AVAILABLE,
  rc_price: null,
  rc_cost: null,
  rc_currency_ref: webSiteStore.getDefaultCurrency()?.id,
  rc_unit_of_measure_ref: webSiteStore.getDefaultUnitOfMeasure()?.id,
  rc_vat_type_ref: webSiteStore.getDefaultVatType()?.id,
  ...useClone(props.modelValue),
})

const { isUpdated, isUpdating, setOriginal } = useIsUpdated(editItem)

const groupStructureTypes = portalStore.getLocalizedServiceStructureTypes().filter(t =>
  t.id === portalStore.serviceStructureTypeEnum.GROUP_PHYSICAL ||
  t.id === portalStore.serviceStructureTypeEnum.GROUP_VIRTUAL
)

const isGroupPhysical = computed(() => editItem.value.structure_type === portalStore.serviceStructureTypeEnum.GROUP_PHYSICAL)

// ── Component helpers ──────────────────────────────────────────────────────

const normalizeComponent = (component) => {
  if (component == null) return null
  if (typeof component === 'number') return { id: component, quantity: 1 }
  if (component.id == null) return null
  const quantity = component.quantity !== '' && component.quantity != null ? Number(component.quantity) : 1
  return { id: component.id, quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1 }
}
const normalizeComponents = (components) => (components || []).map(normalizeComponent).filter(Boolean)

const allServices = computed(() => planningStore.getServices())

const availableComponents = computed(() => {
  const selectedIds = new Set(normalizeComponents(editItem.value.components).map(c => c.id))
  const baseOptions = allServices.value.filter(
    s => s.id !== editItem.value.id && !s.archived && !selectedIds.has(s.id)
  )
  if (isGroupPhysical.value) {
    return baseOptions.filter(s =>
      s.structure_type === portalStore.serviceStructureTypeEnum.PHYSICAL ||
      s.structure_type === portalStore.serviceStructureTypeEnum.GROUP_PHYSICAL
    )
  }
  return baseOptions
})

const filteredComponents = ref([])

const onComponentFilter = (val, update) => {
  update(() => {
    const needle = (val || '').toLowerCase()
    filteredComponents.value = availableComponents.value.filter(s =>
      planningStore.getServiceName(s).toLowerCase().includes(needle)
    )
  })
}

// Submit can be triggered from any step (edit mode's Update button), where the components
// field is unmounted; jump back to it so its standard validation surfaces.
const ensureComponents = async () => {
  if (normalizeComponents(editItem.value.components).length > 0) return true
  step.value = 3
  await nextTick()
  await form.value?.validate()
  return false
}

// ── Structure type change ─────────────────────────────────────────────────

const onStructureTypeChange = () => {
  if (isGroupPhysical.value) {
    const allowedIds = new Set(
      allServices.value
        .filter(s => s.id !== editItem.value.id && (
          s.structure_type === portalStore.serviceStructureTypeEnum.PHYSICAL ||
          s.structure_type === portalStore.serviceStructureTypeEnum.GROUP_PHYSICAL
        ))
        .map(s => s.id)
    )
    editItem.value.components = normalizeComponents(editItem.value.components).filter(c => allowedIds.has(c.id))
  }
}

// ── Tags ──────────────────────────────────────────────────────────────────

const addServiceTag = async (value, doneFn) => {
  const existing = planningStore.getLocalizedServiceTags().find(
    t => t.name.toLowerCase() === value.toLowerCase()
  )
  if (existing) { doneFn(existing.id); return }
  const created = (await planningStore.addServiceTagAsync(buildTagPayload(value, { name: value }))).data
  await planningStore.fetchServiceTagsAsync({ all: true }, true)
  doneFn(created.id)
}
const addPrimaryServiceTag = (value, doneFn) => addServiceTag(value, doneFn)

// ── Currency / Cost ───────────────────────────────────────────────────────

const currentCurrency = computed(() =>
  webSiteStore.currencies.find(i => i.id === editItem.value.currency_ref) || webSiteStore.getDefaultCurrency()
)

const onCurrencyChange = (val) => {
  editItem.value.currency_ref = val
  if (!val) {
    editItem.value.cost = null
  } else {
    editItem.value.cost = editItem.value.cost || 0
  }
}

const onCostChange = (value) => {
  if (!editItem.value.currency_ref) {
    editItem.value.currency_ref = webSiteStore.getDefaultCurrency().id
  }
  editItem.value.cost = value
}

// ── Lifecycle ─────────────────────────────────────────────────────────────

onMounted(async () => {
  await Promise.all([
    planningStore.fetchServiceTagsAsync({ all: true }, true),
    planningStore.fetchEquipmentsAsync({ all: true }, true),
    planningStore.fetchStaffsAsync({ all: true }, true),
    planningStore.fetchGroupsAsync({ all: true }, true),
  ])
  if (props.modelValue.id) {
    editItem.value = { ...(await planningStore.fetchGroupByIdAsync(props.modelValue.id)).data }
    editItem.value.components = normalizeComponents(editItem.value.components)
    setOriginal(editItem.value)
    Object.assign(editItem.value, props.changes)
  } else {
    // Prefill the code with the next database id (editable hint).
    editItem.value.code = String(await planningStore.fetchNextGroupIdAsync())
    setOriginal(editItem.value)
  }
})

// ── Archive / Delete ──────────────────────────────────────────────────────

const { archiveWarning, archiveAsync, onRowRemoving: onDelete } = useArchive({
  onArchive: async(id) => { await planningStore.archiveGroupAsync(id) },
  onDelete: async(id, config) => {
    await planningStore.deleteGroupAsync(id, config)
    notifySuccess(i18n.t('info.deletedSuccessfully'))
    emit('delete')
  },
})

const showDeleteDialog = ref(false)
const onConfirmDelete = () => { showDeleteDialog.value = true }

// ── Attachments ───────────────────────────────────────────────────────────

const onDownloadAttachment = async (file) => {
  try {
    await planningStore.downloadServiceFileAsync(file.id, file.name)
  } catch (error) {
    console.error('Error downloading file:', error)
  }
}

// ── Save ──────────────────────────────────────────────────────────────────

const onUpdate = async () => {
  isUpdating.value = true
  const updateItem = { ...editItem.value }
  if (!editItem.value.avatar_file?.payload) delete updateItem.avatar_file
  updateItem.components = normalizeComponents(editItem.value.components)

  try {
    const saved = await planningStore.upsertGroupAsync(updateItem)
    updateItem.id = updateItem.id || saved.data.id
    notifySuccess(editItem.value.id ? i18n.t('info.updatedSuccessfully') : i18n.t('info.createdSuccessfully'))
    emit('update', updateItem)
    isUpdating.value = false
  } catch (error) {
    console.error(error)
    notifyError(i18n.t('error.saveFailed'))
    isUpdating.value = false
  }
}

const onSubmit = async () => {
  if (!(await form.value?.validate())) return
  if (!(await ensureComponents())) return
  await onUpdate()
}

const emit = defineEmits(['update', 'cancel', 'delete'])
useNavigator(() => emit('cancel'))
useEscapeClose(isUpdated, () => emit('cancel'))
const onCancel = () => emit('cancel')
</script>

<template>
  <q-card v-drag-dialog="!isMobile" class="dialog-card-flex">
    <archive-warning-dialog
      v-model="archiveWarning"
      @confirm="async() => { await archiveAsync(archiveWarning.item.id, false); emit('update') }"
    />
    <confirm-dialog
      v-model="showDeleteDialog"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDelete({ id: editItem.id })"
    />
    <navigate-back
      :text="editItem?.id ? i18n.t('common.editGroup') : i18n.t('common.addGroup')"
      @cancel="onCancel"
    />
    <q-card-section class="scroll col q-pa-none dialog-card-content-scroll" :class="{ 'dialog-card-content': editItem?.id }">
      <q-card-section class="q-px-sm q-pt-none">
        <q-form ref="form">
          <q-stepper v-model="step" class="q-py-none">

            <q-step
              :name="1"
              :title="i18n.t('common.basicInfo')"
              icon="mdi-folder-plus"
              :done="!!editItem.id || step > 1"
              :header-nav="!!editItem.id || step > 1"
            >
              <div class="row q-col-gutter-sm">
                <q-input
                  v-model="editItem.code"
                  :class="isMobile ? 'col-4' : 'col-2'"
                  :label="i18n.t('common.code')"
                  maxlength="20"
                />
                <q-input
                  v-model="editItem.name"
                  :class="isMobile ? 'col-8' : 'col-6'"
                  :label="i18n.t('common.name')"
                  autofocus
                  required
                  :rules="[val => val && val.length > 0 || i18n.t('common.required')]"
                />
                <q-select
                  v-model="editItem.structure_type"
                  :class="isMobile ? 'col-12' : 'col-4'"
                  :label="i18n.t('common.tipology')"
                  option-value="id"
                  option-label="name"
                  :options="groupStructureTypes"
                  emit-value
                  map-options
                  required
                  :rules="[val => val != null || i18n.t('common.required')]"
                  @update:model-value="onStructureTypeChange"
                >
                  <template #selected-item="scope">
                    <chip-template :values="getServiceStructureTypeChip(scope.opt.id)" size="sm" />
                  </template>
                  <template #option="scope">
                    <q-item v-bind="scope.itemProps">
                      <q-item-section>
                        <chip-template :values="[scope.opt]" size="sm" />
                      </q-item-section>
                    </q-item>
                  </template>
                </q-select>
                <form-section-title :label="i18n.t('common.details')" />
                <q-input
                  v-model="editItem.description"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.description')"
                  autogrow
                />
                <q-select
                  :model-value="editItem.tags"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.tag', 2)"
                  :options="planningStore.getLocalizedServiceTags()"
                  option-value="id"
                  option-label="name"
                  use-input
                  use-chips
                  multiple
                  new-value-mode="add-unique"
                  @new-value="addPrimaryServiceTag"
                  @update:model-value="(value) => { editItem.tags = value }"
                >
                  <template #selected-item="chip">
                    <q-chip
                      removable
                      size="sm"
                      :tabindex="chip.tabindex"
                      class="q-ma-none"
                      :style="{ 'background-color': stringToColor(chip.opt.name), 'color': textColor(stringToColor(chip.opt.name)), margin: 0, 'margin-right': '.25rem' }"
                      @remove="chip.removeAtIndex(chip.index)"
                    >
                      {{ chip.opt.name }}
                    </q-chip>
                  </template>
                </q-select>
              </div>
              <stepper-navigation
                :continue="() => { markAttempted(1); form.validate().then((success) => { if (success) nextStep(1) }) }"
              />
            </q-step>

            <q-step
              :name="2"
              :title="i18n.t('common.picture')"
              icon="mdi-camera"
              :done="!!editItem.id || step > 2"
              :header-nav="!!editItem.id || step > 2"
            >
              <div class="row q-col-gutter-sm justify-center">
                <div :class="isMobile ? 'col-12' : 'col-4'" class="flex flex-center">
                  <CameraGalleryInput ref="cameraGalleryInput" :edit-item="editItem" />
                  <q-btn
                    v-if="!editItem.avatar_file && !editItem.avatar_url"
                    icon="mdi-camera"
                    size="2rem"
                    round
                    @click="cameraGalleryInput.onActivateCamera()"
                  />
                  <q-avatar
                    v-else
                    size="10rem"
                    style="cursor: pointer;"
                    @click="cameraGalleryInput.onActivateCamera()"
                  >
                    <q-img
                      loading="lazy"
                      fit="contain"
                      :src="editItem.avatar_file?.payload || editItem.avatar_file || editItem.avatar_url"
                    >
                      <div class="absolute-full text-subtitle2 flex flex-center image-camera">
                        <q-icon name="mdi-camera" size="2rem" />
                      </div>
                    </q-img>
                  </q-avatar>
                </div>
              </div>
              <stepper-navigation
                :continue="() => nextStep(1)"
                :back="prevStep"
              />
            </q-step>

            <q-step
              :name="3"
              :title="i18n.t('common.components')"
              icon="mdi-select-group"
              :done="!!editItem.id || step > 3"
              :header-nav="!!editItem.id || step > 3"
            >
              <service-multi-select
                :model-value="editItem.components"
                :label="i18n.t('common.addComponent')"
                :options="filteredComponents"
                required
                @update:model-value="editItem.components = $event"
                @filter="onComponentFilter"
              />
              <stepper-navigation
                :continue="() => { form.validate().then(success => success && nextStep(1)) }"
                :back="prevStep"
              />
            </q-step>

            <q-step
              :name="4"
              :title="i18n.t('common.additionalInfo')"
              icon="mdi-information-outline"
              :done="!!editItem.id || step > 4"
              :header-nav="!!editItem.id || step > 4"
            >
              <div v-if="isGroupPhysical" class="row q-col-gutter-sm">
                <q-select
                  :model-value="editItem.currency_ref"
                  class="col-6"
                  :label="i18n.t('common.currency')"
                  option-value="id"
                  option-label="code"
                  clearable
                  :options="webSiteStore.currencies"
                  @update:model-value="onCurrencyChange"
                />
                <currency-input
                  :model-value="editItem.cost"
                  class="col-6"
                  :label="i18n.t('common.cost')"
                  :suffix="currentCurrency.symbol"
                  @update:model-value="onCostChange"
                />
              </div>
              <file-url-edit
                bordered
                caption
                :model-value="editItem.service_files"
                class="q-mt-sm"
                :mobile="isMobile"
                @update:model-value="editItem.service_files = $event"
                @download="onDownloadAttachment"
              />
              <stepper-navigation
                :continue="() => { markAttempted(4); form.validate().then(success => success && nextStep()) }"
                :back="prevStep"
              />
            </q-step>

            <q-step
              :name="5"
              :title="i18n.t('common.rateCardInfo')"
              icon="mdi-currency-usd"
              :done="!!editItem.id || step > 5"
              :header-nav="!!editItem.id || step > 5"
            >
              <div class="q-mb-sm">
                <q-icon
                  name="mdi-information-outline"
                  color="info"
                  size="sm"
                  class="cursor-pointer"
                >
                  <q-tooltip max-width="18.75rem" class="text-body2">
                    {{ i18n.t('common.rateCardGenericListingHint') }}
                    <router-link :to="{ name: 'listings' }" class="text-primary text-weight-medium" style="text-decoration: none;">
                      {{ i18n.t('common.listing', 2) }}
                      <q-icon name="mdi-open-in-new" size="xs" />
                    </router-link>
                  </q-tooltip>
                </q-icon>
              </div>
              <rate-card-info
                :model-value="editItem"
                :mobile="isMobile"
                @update:model-value="editItem = $event"
              />
              <stepper-navigation
                :back="prevStep"
                :finish="editItem.id ? null : onSubmit"
                :finish-label="i18n.t('common.addGroup')"
                :loading="isUpdating"
              />
            </q-step>

          </q-stepper>
        </q-form>
      </q-card-section>
    </q-card-section>
    <q-card-actions v-if="editItem?.id" class="align-center dialog-card-bottom-bar">
      <q-btn
        class="large-btn q-mr-sm"
        color="negative"
        :label="i18n.t('common.delete')"
        @click="onConfirmDelete"
      />
      <q-btn
        v-if="isUpdated"
        class="large-btn"
        color="positive"
        :disable="!editItem.name"
        :label="i18n.t('common.update')"
        :loading="isUpdating"
        @click="onSubmit"
      />
    </q-card-actions>
  </q-card>
</template>

<style scoped>
</style>
